/**
 * ana.js — ANA Alcazar Theme Customizing
 * ============================================================================
 * Diese Datei enthaelt alle ANA-spezifischen JS-Anpassungen, die ueber das
 * Vanilla-Symmetry-Theme hinausgehen. main.js bleibt reines Vanilla Symmetry
 * 8.3.1; ana.js wird in layout/theme.liquid NACH main.js mit `defer` geladen.
 *
 * Inhalt:
 *   1. <product-inventory>  Custom-Element  (ANA-only — nicht in Vanilla 8.3.1)
 *   2. <variant-content>    Custom-Element  (ANA-only — nicht in Vanilla 8.3.1)
 *
 * Loading-Reihenfolge:
 *   - main.js (Vanilla 8.3.1) zuerst
 *   - ana.js (diese Datei) danach
 *
 * Konfliktschutz:
 *   - Beide Custom-Elements werden mit `customElements.get()` gegen doppelte
 *     Registrierung abgesichert. Falls Vanilla in einer spaeteren Version
 *     gleichnamige Elemente einfuehrt, wird die ANA-Version NICHT registriert
 *     (TODO PHASE 3 — QA: pruefen).
 *
 * Verwendete Globals (aus main.js):
 *   - window.initLazyScript
 * ============================================================================
 */

(() => {
  /**
   * <product-inventory>
   * --------------------------------------------------------------------------
   * ANA-Custom-Element fuer die Anzeige der Lagerbestand-Notice auf PDP.
   * Reagiert auf 'on:variant:change' und blendet einen lagerbasierten
   * Hinweistext ein/aus. Erwartet Inventory-Daten als JSON in
   * <script type="application/json"> innerhalb des Elements.
   *
   * Verwendung:
   *   - snippets/product-inventory.liquid
   *   - sections/main-product.liquid / main-product-ana.liquid
   *   - sections/featured-product.liquid / featured-product-ana.liquid
   *   - snippets/cart-item.liquid, product-label-detail.liquid (CSS-Klassen)
   */
  if (!customElements.get('product-inventory')) {
    class ProductInventory extends HTMLElement {
      constructor() {
        super();
        window.initLazyScript(this, this.initLazySection.bind(this));
      }

      initLazySection() {
        this.threshold = parseInt(this.dataset.threshold, 10);
        this.productInventory = this.querySelector('.product-inventory');
        this.inventoryNotice = this.querySelector('.product-inventory__status');
        this.variantInventory = this.getVariantInventory();

        this.closest('.js-product').addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      }

      /**
       * Gets the inventory data for all product variants
       * @returns {?object}
       */
      getVariantInventory() {
        const dataEl = this.querySelector('[type="application/json"]');
        return this.variantInventory || JSON.parse(dataEl.textContent);
      }

      /**
       * Handles a 'change' event on the variant picker element
       * @param {object} evt - Event object
       */
      handleVariantChange(evt) {
        this.updateInventory(
          evt.detail.variant
            ? this.variantInventory.find((v) => v.id === evt.detail.variant.id)
            : null
        );
      }

      /**
       *
       * Updates the inventory notice
       * @param {?object} inventory - the inventory data
       */
      updateInventory(inventory) {
        if (!inventory) {
          this.productInventory.hidden = true;
          return;
        }

        const count = inventory.inventory_quantity;
        const showCount = this.dataset.showInventoryCount === 'always'
          || (
            this.dataset.showInventoryCount === 'low'
            && count <= this.threshold
          );

        let notice = null;
        if (showCount) {
          if (count <= this.threshold) {
            notice = this.dataset.textXLeftLow.replace('[QTY]', count);
          } else {
            notice = this.dataset.textXLeftOk.replace('[QTY]', count);
          }
        } else {
          // eslint-disable-next-line no-lonely-if
          if (count <= this.threshold) {
            notice = this.dataset.textLow;
          } else {
            notice = this.dataset.textOk;
          }
        }

        this.productInventory.classList.toggle('product-inventory--low', count <= this.threshold);
        this.productInventory.classList.toggle('product-inventory--ok', count > this.threshold);
        this.inventoryNotice.innerText = notice;
        this.productInventory.hidden = false;
      }
    }

    customElements.define('product-inventory', ProductInventory);
  }

  /**
   * <variant-content>
   * --------------------------------------------------------------------------
   * ANA-Custom-Element fuer variantenabhaengige Inhalte auf PDP.
   * Wechselt Inhalts-Snippets basierend auf der ausgewaehlten Variante.
   * Innerhalb des Elements stehen mehrere Templates mit data-variant=ID,
   * von denen jeweils das passende geklont und eingefuegt wird.
   *
   * Verwendung:
   *   - sections/main-product.liquid / main-product-ana.liquid
   *   - sections/featured-product.liquid / featured-product-ana.liquid
   */
  if (!customElements.get('variant-content')) {
    class VariantContent extends HTMLElement {
      constructor() {
        super();
        if (this.childElementCount > 0) {
          this.closest('.js-product').addEventListener('on:variant:change', this.handleVariantChange.bind(this));
        }
      }

      /**
       * Handles a 'change' event on the variant picker element
       * @param {object} evt - Event object
       */
      handleVariantChange(evt) {
        [...this.childNodes].filter((el) => el.tagName !== 'SCRIPT').forEach((el) => el.remove());
        this.querySelectorAll(':not(script)').forEach((el) => el.remove());
        const contentToShow = this.querySelector(`[data-variant="${evt.detail.variant ? evt.detail.variant.id : ''}"]`);
        if (contentToShow) {
          this.insertAdjacentHTML('beforeend', contentToShow.innerHTML);
        }
      }
    }

    customElements.define('variant-content', VariantContent);
  }
})();
